import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMarketStore } from '../../stores/useMarketStore'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'

export const Market: React.FC = () => {
  const { t } = useTranslation()
  const { shops, shopProducts, isLoading, loadShops, loadShopProducts } = useMarketStore()
  const [selectedShop, setSelectedShop] = useState<string | null>(null)

  useEffect(() => {
    loadShops()
  }, [])

  useEffect(() => {
    if (selectedShop) {
      loadShopProducts(selectedShop)
    }
  }, [selectedShop])

  if (isLoading) {
    return (
      <Card title={`🏪 ${t('market.title')}`} subtitle={t('market.loading')}>
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card title={`🏪 ${t('market.title')}`} subtitle={t('market.subtitle')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shops.map((shop) => (
            <div
              key={shop.id}
              className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                selectedShop === shop.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedShop(shop.id)}
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-800">{shop.name}</h3>
                <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded capitalize">
                  {shop.type}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                {t('market.demand')}: {shop.base_demand_multiplier}x
              </p>
              <p className="text-xs text-gray-500">
                {t('market.location')}: ({shop.location_x}, {shop.location_y})
              </p>
            </div>
          ))}
        </div>
      </Card>

      {selectedShop && (
        <Card title={t('market.sellableProducts')} subtitle={`${t('market.shop')}: ${shops.find(s => s.id === selectedShop)?.name}`}>
          <div className="space-y-3">
            {shopProducts.length > 0 ? (
              shopProducts.map((shopProduct) => (
                <div key={shopProduct.product_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium">{shopProduct.product?.name}</h4>
                    <p className="text-sm text-gray-600">
                      {t('market.basePrice')}: {shopProduct.product?.base_price} {t('common.price')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('market.shopInterest')}: {shopProduct.base_interest}x | {t('market.priceMultiplier')}: {shopProduct.price_multiplier}x
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-green-600">
                      {Math.round((shopProduct.product?.base_price || 0) * shopProduct.price_multiplier)} {t('common.price')}
                    </p>
                    <Button size="sm" variant="primary">
                      {t('market.sell')}
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                {t('market.noProducts')}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Market Stats */}
      <Card title={`📊 ${t('market.statistics')}`} subtitle={t('market.statisticsSubtitle')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <h3 className="text-2xl font-bold text-blue-600">{shops.length}</h3>
            <p className="text-sm text-blue-800">{t('market.activeShops')}</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <h3 className="text-2xl font-bold text-green-600">
              {shops.reduce((sum, shop) => sum + shop.base_demand_multiplier, 0).toFixed(1)}
            </h3>
            <p className="text-sm text-green-800">{t('market.averageDemand')}</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <h3 className="text-2xl font-bold text-purple-600">
              {shopProducts.length}
            </h3>
            <p className="text-sm text-purple-800">{t('market.sellableProductsCount')}</p>
          </div>
        </div>
      </Card>
    </div>
  )
}
